import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from 'react'
import { loginString, useEIP712Sign, validationSigner } from '@provex/eip712-utils'
import { useAccount } from 'wagmi'
import { useQuery } from '@tanstack/react-query'
import { useToaster } from '../utils/toast'
import type { Hex } from 'viem'
import { MAX_SIGNATURE_AGE_MS } from 'sacrifice-utils'
import { getProjectIdFromPath } from '../utils/active-project'

// Per-campaign authentication blob. The login signature binds to a specific
// campaign (projectId + that campaign's disclaimer terms), so each campaign
// gets its own storage slot keyed by projectId.
export type AuthData = {
  signature: Hex
  inputs: { timestamp: bigint }
  projectId: string
  termsOfUse: string
}

// LocalStorage utilities
const AUTH_STORAGE_KEY_PREFIX = 'sacrifice-auth'
/** Builds the localStorage key namespacing auth state by campaign projectId. */
export const authStorageKey = (projectId: string) => `${AUTH_STORAGE_KEY_PREFIX}:${projectId}`

const saveAuthToStorage = (authData: AuthData) => {
  try {
    const serialized = {
      signature: authData.signature,
      inputs: {
        timestamp: authData.inputs.timestamp.toString(),
      },
      projectId: authData.projectId,
      termsOfUse: authData.termsOfUse,
    }
    localStorage.setItem(authStorageKey(authData.projectId), JSON.stringify(serialized))
  } catch (error) {
    console.warn('Failed to save auth to localStorage:', error)
  }
}

const loadAuthFromStorage = (projectId: string): AuthData | null => {
  try {
    const stored = localStorage.getItem(authStorageKey(projectId))
    if (!stored) return null

    const parsed = JSON.parse(stored)
    return {
      signature: parsed.signature,
      inputs: {
        timestamp: BigInt(parsed.inputs.timestamp),
      },
      projectId: parsed.projectId,
      termsOfUse: parsed.termsOfUse,
    }
  } catch (error) {
    console.warn('Failed to load auth from localStorage:', error)
    return null
  }
}

/**
 * Single source of truth for "is this login signature still fresh?".
 *
 * A login signature is fresh while its age (now − issued-at) has not exceeded
 * MAX_SIGNATURE_AGE_MS. The comparison is INCLUSIVE (a signature at exactly the
 * max age is still fresh) so every call site — the React Query gate, the
 * mount-restore, the reconciler, and the displayed `isSignedIn` — agrees on the
 * boundary by construction (they previously mixed strict `<` and inclusive `<=`,
 * which could disagree by a millisecond).
 *
 * @param timestampMs - The signature's issued-at time in epoch milliseconds.
 * @param nowMs - The current time in epoch milliseconds.
 * @returns True while the signature has not expired.
 */
export const isAuthFresh = (timestampMs: number, nowMs: number): boolean =>
  nowMs - timestampMs <= MAX_SIGNATURE_AGE_MS

/**
 * Pure reconciliation of in-memory auth against the active campaign.
 *
 * Returns the next `authData`:
 * - the current blob unchanged when it already matches the active campaign;
 * - the active campaign's stored blob when present and not expired;
 * - `null` otherwise (drops a stale blob for a campaign we navigated away from).
 *
 * Never touches storage — switching campaigns must not delete another campaign's
 * saved login. `loadStored` and `now` are injected for testability.
 *
 * @param current - The current in-memory auth blob, or null.
 * @param activeProjectId - The campaign id derived from the active route.
 * @param loadStored - Reads a campaign's persisted blob (or null).
 * @param now - Current epoch milliseconds.
 * @returns The reconciled auth blob, or null.
 */
export const reconcileAuthData = (
  current: AuthData | null,
  activeProjectId: string,
  loadStored: (projectId: string) => AuthData | null,
  now: number
): AuthData | null => {
  if (current?.projectId === activeProjectId) return current

  const stored = loadStored(activeProjectId)
  if (stored && isAuthFresh(Number(stored.inputs.timestamp), now)) {
    return stored
  }
  return null
}

// const saveAddressesToStorage = (addresses: SacrificeInfo[]) => {
//   // if (!isLocalhost()) return

//   try {
//     localStorage.setItem(ADDRESSES_STORAGE_KEY, JSON.stringify(addresses))
//   } catch (error) {
//     console.warn('Failed to save addresses to localStorage:', error)
//   }
// }

// const loadAddressesFromStorage = (): SacrificeInfo[] | null => {
//   // if (!isLocalhost()) return null

//   try {
//     const stored = localStorage.getItem(ADDRESSES_STORAGE_KEY)
//     if (!stored) return null

//     return JSON.parse(stored)
//   } catch (error) {
//     console.warn('Failed to load addresses from localStorage:', error)
//     return null
//   }
// }

const clearAuthStorage = (projectId: string) => {
  try {
    localStorage.removeItem(authStorageKey(projectId))
  } catch (error) {
    console.warn('Failed to clear auth from localStorage:', error)
  }
}

interface AuthContextValue {
  // Authentication state
  authData: AuthData | null
  isSignedIn: boolean
  isExpired: boolean

  connectedAddress: string | null
  recoveredAddress: string | null
  targetAddress: string | null
  address: string | null

  // Auth methods
  signIn: (args: { projectId: string; termsOfUse: string }) => Promise<void>
  signOut: () => void
  setTargetAddress: (address: string | null) => void

  // For other contexts to use
  setAuth: (args: {
    signature: Hex
    timestamp: bigint
    projectId: string
    termsOfUse: string
  }) => void
  clearAuth: () => void
  /** Reconciles in-memory auth to the active campaign (load stored / drop stale). */
  reconcileAuthForProject: (projectId: string) => void
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

interface AuthProviderProps {
  children: React.ReactNode
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const toaster = useToaster()
  // useAccount will return null/undefined until WagmiProvider loads
  // This is fine - AuthProvider works without wallet connection initially
  const { address: connectedAddress = null } = useAccount()
  const [targetAddress, setTargetAddress] = useState<string | null>(null)

  // Auth state management
  const [authData, setAuthData] = useState<AuthData | null>(null)
  // Mirror authData into a ref so reconcileAuthForProject can read the latest
  // value while keeping a stable identity. Written during render (the idiomatic
  // latest-ref pattern) so there is no one-commit lag — reconciliation never
  // sees a stale blob on first paint.
  const authDataRef = useRef<AuthData | null>(null)
  authDataRef.current = authData

  const authKeySuffix = useMemo(
    () => [authData?.signature ?? '', authData?.inputs.timestamp?.toString() ?? ''],
    [authData]
  )

  const authDataIsAvailable = useMemo(
    () =>
      // assume that if auth data is available, it is also valid
      !!authData?.signature &&
      !!authData?.inputs.timestamp &&
      isAuthFresh(Number(authData.inputs.timestamp), Date.now()),
    [authData]
  )

  // Query to recover signer address from signature
  const { data: recoveredAddress = null } = useQuery({
    queryKey: ['recoveredAddress', ...authKeySuffix],
    queryFn: async () => {
      return await validationSigner.recoverSigner(authData!.signature, {
        purpose: loginString,
        projectId: authData!.projectId,
        timestamp: authData!.inputs.timestamp,
        termsOfUse: authData!.termsOfUse,
      })
    },
    enabled: authDataIsAvailable,
    staleTime: Infinity, // Never refetch - signature recovery is deterministic
    gcTime: 60 * 60 * 1000, // Keep in cache for 1 hour
  })

  const address = useMemo(() => {
    return targetAddress ?? connectedAddress ?? recoveredAddress ?? null
  }, [targetAddress, connectedAddress, recoveredAddress])

  // Load auth data from localStorage on mount.
  // AuthProvider is mounted ABOVE ProjectProvider, so the active campaign must
  // be derived from the URL path (not from ProjectContext) to pick the right
  // per-campaign storage slot.
  useEffect(() => {
    // Drop any stale pre-W1 single-key blob so it can't linger.
    try {
      localStorage.removeItem(AUTH_STORAGE_KEY_PREFIX)
    } catch {
      // ignore
    }

    const activeProjectId = getProjectIdFromPath(window.location.pathname)
    if (!activeProjectId) return

    const storedAuth = loadAuthFromStorage(activeProjectId)
    if (!storedAuth) return

    // Check if stored auth is still valid (not expired)
    const isValid = isAuthFresh(Number(storedAuth.inputs.timestamp), Date.now())

    if (isValid) {
      console.log('🔄 Restoring auth from localStorage for campaign', activeProjectId)
      setAuthData(storedAuth)
      // User addresses will be fetched by React Query when authData is set
    } else {
      console.log('⏰ Stored auth expired, clearing localStorage')
      clearAuthStorage(activeProjectId)
      setAuthData(null)
    }
  }, [])

  // Save auth data to localStorage when it changes, keyed by its campaign.
  useEffect(() => {
    if (authData) {
      saveAuthToStorage(authData)
    }
  }, [authData])

  // Addresses are now saved to localStorage in the React Query queryFn

  // Derive authentication state with timestamp validation (1 hour expiry)
  const { isSignedIn, isExpired } = useMemo(() => {
    if (!authData || !authData.inputs.timestamp) {
      return { isSignedIn: false, isExpired: false }
    }

    // Check if the timestamp is not too old (1 hour)
    const isValid = isAuthFresh(Number(authData.inputs.timestamp), Date.now())

    return {
      isSignedIn: isValid,
      isExpired: !isValid,
    }
  }, [authData])

  // Auto-clear expired authentication
  useEffect(() => {
    if (authData && isExpired && authData.inputs.timestamp) {
      console.log('Authentication expired, clearing auth data')
      clearAuthStorage(authData.projectId)
      setAuthData(null)
    }
  }, [authData, isExpired])

  // Auto-clear authentication when connected address changes and doesn't match recovered address
  // Note: toaster is intentionally NOT in the dependency array to prevent infinite loops
  // We only want to run this when authentication state changes, not when the toaster object changes
  useEffect(() => {
    // If no auth data, nothing to clear
    if (!authData) {
      return
    }

    // If wallet is disconnected, clear auth
    if (!connectedAddress) {
      console.log('🔒 Wallet disconnected - clearing authentication')
      clearAuthStorage(authData.projectId)
      setAuthData(null)
      return
    }

    // If we don't have a recovered address yet, wait for it
    if (!recoveredAddress) {
      return
    }

    // Check if connected address matches the recovered address (case-insensitive)
    const addressesMatch = connectedAddress.toLowerCase() === recoveredAddress.toLowerCase()

    if (!addressesMatch) {
      console.log(
        '🔒 Connected address changed - clearing authentication',
        '\n  Connected:',
        connectedAddress,
        '\n  Authenticated:',
        recoveredAddress
      )
      clearAuthStorage(authData.projectId)
      setAuthData(null)
      toaster.warning({
        title: 'Wallet Changed',
        description: 'Your connected wallet has changed. Please sign in again with the new wallet.',
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectedAddress, recoveredAddress, authData])

  // Get the current project's signing function
  const { signValidate } = useEIP712Sign()

  const signIn = useCallback(
    async ({ projectId, termsOfUse }: { projectId: string; termsOfUse: string }) => {
      if (!connectedAddress) {
        toaster.error({
          title: 'Wallet not connected',
          description: 'Please connect your wallet first',
        })
        return
      }

      if (!projectId) {
        toaster.error({
          title: 'No campaign selected',
          description: 'Please select a campaign first',
        })
        return
      }

      try {
        // The login signature binds to the active campaign: projectId + that
        // campaign's disclaimer terms. The backend reconstructs verification
        // with the same values, so both sides must agree exactly.
        const timestamp = BigInt(Date.now())
        const signatureResult = await signValidate({ projectId, termsOfUse, timestamp }).catch(
          () => null
        )

        if (!signatureResult) {
          return
        }

        const tempRecoveredAddress = await validationSigner.recoverSigner(signatureResult, {
          purpose: loginString,
          projectId,
          timestamp,
          termsOfUse,
        })

        if (tempRecoveredAddress !== connectedAddress) {
          console.error('Address mismatch:', tempRecoveredAddress, 'vs', connectedAddress)
          throw new Error('Invalid signature')
        }
        // Removed a FullStory `window.FS('setIdentity', ...)` analytics call here.
        // It is undefined whenever FullStory is blocked (ad-blocker, privacy mode,
        // not yet loaded) — common for a crypto audience — and being inside this
        // try block it threw before setAuthData ran, so a blocked analytics script
        // silently failed the entire sign-in and showed a misleading "Signing
        // failed" toast. If reinstated, it must be guarded and kept off the auth path.

        // Set authentication data - React Query will automatically fetch user addresses
        setAuthData({ signature: signatureResult, inputs: { timestamp }, projectId, termsOfUse })

        toaster.success({
          title: 'Successfully signed in',
          description: 'You can now generate sacrifice addresses',
        })
      } catch (error) {
        console.error('Signing failed:', error)
        toaster.error({
          title: 'Signing failed',
          description: 'Please try again',
        })
      }
    },
    [connectedAddress, signValidate, toaster]
  )

  const signOut = useCallback(() => {
    if (!authData) return
    clearAuthStorage(authData.projectId)
    setAuthData(null)
    toaster.success({
      title: 'Signed out',
      description: 'You have been signed out successfully',
    })
  }, [authData, toaster])

  const setAuth = useCallback(
    ({
      signature,
      timestamp,
      projectId,
      termsOfUse,
    }: {
      signature: Hex
      timestamp: bigint
      projectId: string
      termsOfUse: string
    }) => {
      // Set auth data - React Query will automatically fetch user addresses
      setAuthData({ signature, inputs: { timestamp }, projectId, termsOfUse })
    },
    []
  )

  const clearAuth = useCallback(() => {
    setAuthData(prev => {
      if (prev) clearAuthStorage(prev.projectId)
      return null
    })
  }, [])

  const reconcileAuthForProject = useCallback((projectId: string) => {
    // Read storage + clock once, outside the (pure) state updater.
    const next = reconcileAuthData(authDataRef.current, projectId, loadAuthFromStorage, Date.now())
    setAuthData(prev => (prev === next ? prev : next))
  }, [])

  const value: AuthContextValue = {
    authData,
    isSignedIn,
    isExpired,
    connectedAddress,
    recoveredAddress,
    targetAddress,
    address,
    signIn,
    signOut,
    setTargetAddress,
    setAuth,
    clearAuth,
    reconcileAuthForProject,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
